import { useContext, useEffect, useState } from 'react';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _isObject from 'lodash/isObject';
import _isNaN from 'lodash/isNaN';
import _isEqualWith from 'lodash/isEqualWith';
import moment from 'moment';
import _keys from 'lodash/keys';
import _find from 'lodash/find';
import { GAME_NAME_OPTIONS, GAME_NAMES } from 'pages/constants';

import { AjaxContext, AppContext, EMPTY_ARRAY, formatListData, UserContext } from 'commons-ui';

import { UserDetailsContext } from 'context/userDetails';
import { ACTION, PAGE_NAMES, USER_PROFILE_RESOURCES } from 'constant';
import { REFUND_STATUSES_LABEL, REFUND_STATUSES_MAP, VIEW_FIELD_KEYS } from './constants';

const formatTime = (time: String): String => {
	const arr = time.split(':');
	const newtime = `${arr[0]}:${arr[1]}  ${arr[2].split(' ')[1]}`;
	return newtime;
};

const hydrateTimeFields = (data: any) => {
	const { tournamentStartTime, tournamentEndTime, gameStartTime, gameEndTime, registeredOn, refundedOn } = data;

	return {
		...data,
		tournamentStartTime: tournamentStartTime || undefined,
		tournamentEndTime: tournamentEndTime || undefined,
		gameStartTime: gameStartTime ? formatTime(gameStartTime) : undefined,
		gameEndTime: gameEndTime || undefined,
		registeredOn: registeredOn ? formatTime(registeredOn) : undefined,
		refundedOn: refundedOn ? formatTime(refundedOn) : undefined
	};
};
const convertTimeStringToUnix = (timeString: string): number => {
	const time = moment(timeString, 'DD-MM-YYYY hh:mm:ss.SSS A').valueOf();
	return time;
};

const hydrateData = (data: any, isMtt: boolean, gameName: string | null | undefined) => {
	let formattedData = { ...data };
	if (_has(data, 'payoutDistribution')) {
		if (
			!_isEmpty(gameName) &&
			(_isEqualWith(gameName, GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]) ||
				_isEqualWith(gameName, GAME_NAME_OPTIONS[GAME_NAMES.CT]) ||
				_isEqualWith(gameName, GAME_NAME_OPTIONS[GAME_NAMES.TCNX]))
		) {
			formattedData.payoutDistribution = _get(data, ['payoutDistribution', _keys(_get(data, ['payoutDistribution']))[0]], {});
		} else if (_has(data, 'mttRank') && _has(data, 'maxWinners')) {
			formattedData.payoutDistribution = _get(data, ['payoutDistribution', data.maxWinners], {});
		} else if (_has(data, 'minimumPlayers') || _has(data, 'maximumPlayers')) {
			formattedData.payoutDistribution = _get(
				data,
				['payoutDistribution', _get(data, 'minimumPlayers') || _get(data, 'maximumPlayers')],
				{}
			);
		}
	}

	if (_has(data, 'refunded')) {
		formattedData.refunded = REFUND_STATUSES_LABEL[_get(REFUND_STATUSES_MAP, formattedData.refunded)];
	}

	if (_has(data, 'tournamentStartTime')) {
		formattedData = hydrateTimeFields(formattedData);
	}

	if (!_isEmpty(gameName) && _isEqualWith(gameName, GAME_NAME_OPTIONS[GAME_NAMES.CT])) {
		formattedData.prizePot = JSON.stringify(data.prizePot);
	} else if (_has(data, 'prizePot')) {
		if (_has(data, 'mttRank') && _has(data, 'maximumPlayers')) {
			formattedData.prizePot = _get(data, ['prizePot', data.maximumPlayers]);
		} else if (_has(data, 'minimumPlayers')) {
			formattedData.prizePot = _get(data, ['prizePot', data.minimumPlayers]);
		}
	}

	formattedData.rank = isMtt ? data.mttRank : data.rank;

	return formattedData;
};

const getPaymentBonusCoins = (data: any) => {
	if (!_isNil(data) && !_isEmpty(data)) {
		return _get(data, 'bonusCoins', 0) + _get(data, 'unlockedCoins', 0);
	}
	return 0;
};

const getWinningAmount = (data: any) => {
	if (!_isEmpty(data) && _isObject(data[VIEW_FIELD_KEYS.WINNINGS])) {
		return (
			_get(_get(data, VIEW_FIELD_KEYS.WINNINGS), 'bonusCoins', 0) +
			_get(_get(data, VIEW_FIELD_KEYS.WINNINGS), 'actualCoins', 0) +
			_get(_get(data, VIEW_FIELD_KEYS.WINNINGS), 'addedCoins', 0) +
			_get(_get(data, VIEW_FIELD_KEYS.WINNINGS), 'unlockedCoins', 0)
		);
	}
	if (!_isEmpty(data) && !_isNil(data[VIEW_FIELD_KEYS.WINNINGS]) && !_isNaN(data[VIEW_FIELD_KEYS.WINNINGS])) {
		return data[VIEW_FIELD_KEYS.WINNINGS];
	}
	return 0;
};

const formatAnalyzeTournamentData = (data: any, gameName: string | null | undefined, mttid: number | null | undefined) => {
	return {
		[VIEW_FIELD_KEYS.TABLE_ID]: _get(data, VIEW_FIELD_KEYS.TABLE_ID),
		[VIEW_FIELD_KEYS.ENTRY_FEE]: _get(data, VIEW_FIELD_KEYS.ENTRY_FEE),
		[VIEW_FIELD_KEYS.TOURNAMENT_START_TIME]: _get(data, VIEW_FIELD_KEYS.TOURNAMENT_START_TIME),
		[VIEW_FIELD_KEYS.TOURNAMENT_END_TIME]: _get(data, VIEW_FIELD_KEYS.TOURNAMENT_END_TIME),
		[VIEW_FIELD_KEYS.TOTAL_REGISTRATIONS]: _get(data, VIEW_FIELD_KEYS.TOTAL_REGISTRATIONS),
		[VIEW_FIELD_KEYS.PRIZE_POT]: _get(data, VIEW_FIELD_KEYS.PRIZE_POT),
		[VIEW_FIELD_KEYS.MINIMUM_PLAYERS]: _get(data, VIEW_FIELD_KEYS.MINIMUM_PLAYERS),
		[VIEW_FIELD_KEYS.MAXIMUM_PLAYERS]: _get(data, VIEW_FIELD_KEYS.MAXIMUM_PLAYERS),
		[VIEW_FIELD_KEYS.TYPE]: _get(data, VIEW_FIELD_KEYS.TYPE),
		[VIEW_FIELD_KEYS.PAYOUT_DISTRIBUTION]: _get(data, VIEW_FIELD_KEYS.PAYOUT_DISTRIBUTION),
		[VIEW_FIELD_KEYS.TOURNAMENT_STATUS]: _get(data, VIEW_FIELD_KEYS.TOURNAMENT_STATUS),
		[VIEW_FIELD_KEYS.REFUNDED]: _get(data, VIEW_FIELD_KEYS.REFUNDED),
		[VIEW_FIELD_KEYS.REFUNDED_ON]: _get(data, VIEW_FIELD_KEYS.REFUNDED_ON),
		[VIEW_FIELD_KEYS.USER_GAME_DETAILS]: {
			[VIEW_FIELD_KEYS.REGISTERED_ON]: _get(data, VIEW_FIELD_KEYS.REGISTERED_ON),
			[VIEW_FIELD_KEYS.JOINED_ON]: _get(data, VIEW_FIELD_KEYS.JOINED_ON),
			[VIEW_FIELD_KEYS.GAME_START_TIME]: _get(data, VIEW_FIELD_KEYS.GAME_START_TIME),
			[VIEW_FIELD_KEYS.GAME_END_TIME]: _get(data, VIEW_FIELD_KEYS.GAME_END_TIME),
			...(_isEmpty(gameName) || (!_isEmpty(gameName) && !_isEqualWith(gameName, GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]))
				? { [VIEW_FIELD_KEYS.FINAL_TOKEN_POSITIONS]: _get(data, VIEW_FIELD_KEYS.FINAL_TOKEN_POSITIONS) }
				: { [VIEW_FIELD_KEYS.GAME_BOARD_TOKEN_POSITIONS]: _get(data, VIEW_FIELD_KEYS.GAME_BOARD_TOKEN_POSITIONS) })
		},
		[VIEW_FIELD_KEYS.PAYMENT]: {
			[VIEW_FIELD_KEYS.USER_PAYMENT_TRANSACTION_ID]: _get(data, VIEW_FIELD_KEYS.USER_PAYMENT_TRANSACTION_ID),
			[VIEW_FIELD_KEYS.BONUSMONEY]: (getPaymentBonusCoins(_get(data, VIEW_FIELD_KEYS.PAYMENT)) / 100).toFixed(2),
			[VIEW_FIELD_KEYS.DEPOSIT]: (_get(data, [VIEW_FIELD_KEYS.PAYMENT, 'addedCoins'], 0) / 100).toFixed(2),
			[VIEW_FIELD_KEYS.WINNINGS]: (_get(data, [VIEW_FIELD_KEYS.PAYMENT, 'actualCoins'], 0) / 100).toFixed(2)
		},

		[VIEW_FIELD_KEYS.FINAL_RESULT_FOR_USER]: {
			[VIEW_FIELD_KEYS.WINNING_AMOUNT]:
				(!_isEmpty(gameName) &&
					(_isEqualWith(gameName, GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]) ||
						_isEqualWith(gameName, GAME_NAME_OPTIONS[GAME_NAMES.CT]))) ||
				!_isNil(mttid)
					? getWinningAmount(data)
					: (getWinningAmount(data) / 100).toFixed(2),
			[VIEW_FIELD_KEYS.WINNINGS]: (_get(_get(data, VIEW_FIELD_KEYS.WINNINGS), 'actualCoins', 0) / 100).toFixed(2),
			[VIEW_FIELD_KEYS.BONUSMONEY]: (getPaymentBonusCoins(_get(data, VIEW_FIELD_KEYS.WINNINGS)) / 100).toFixed(2),
			[VIEW_FIELD_KEYS.DEPOSIT]: (_get(_get(data, VIEW_FIELD_KEYS.WINNINGS), 'addedCoins', 0) / 100).toFixed(2),
			[VIEW_FIELD_KEYS.SCORE]: _get(data, VIEW_FIELD_KEYS.SCORE),
			[VIEW_FIELD_KEYS.RANK]: _get(data, VIEW_FIELD_KEYS.RANK)
		}
	};
};

const useService = ({
	ltid,
	mttid,
	cgrid,
	gameName,
	playingAttempt,
	gameType,
	pwfInviteId
}: {
	ltid?: number | null;
	mttid?: number | null;
	cgrid?: number | null;
	gameName?: string | null;
	playingAttempt?: number | null;
	gameType?: string | null;
	pwfInviteId?: string | null;
}) => {
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const [forceUpdateChild, setForceUpdateChild] = useState<boolean>(false);
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const [analyzeData, setAnalyzeData] = useState({});
	const [tournamentStartTime, setTournamentStartTime] = useState('');
	const [tournamentStartTimeInMS, setTournamentStartTimeInMS] = useState(0);
	const [tournamentEndTimeInMS, setTournamentEndTimeInMS] = useState(0);
	const [registeredOnInMS, setRegisteredOnInMS] = useState(0);
	const [tableId, setTableId] = useState('');
	const [gameEndTime, setGameEndTime] = useState('');
	const [gameStartTime, setGameStartTime] = useState('');
	const [gameUid, setGameUid] = useState('');
	const [registeredOn, setRegisteredOn] = useState('');
	const [entryFee, setEntryFee] = useState(0);
	const [transactionId, setTransactionId] = useState(0);
	const [inning, setInning] = useState();
	const [score, setScore] = useState();
	const [winnings, setWinnings] = useState();
	const [resultData, setResultData] = useState();

	const { postData } = useContext(AjaxContext);
	const { app } = useContext(AppContext);
	const {
		selectedUser: { uid, ludoUserId }
	} = useContext(UserDetailsContext);
	const { permissionsConfig } = useContext(UserContext);
	const fetchData = async () => {
		setForceUpdateChild(false);
		setShowLoader(true);
		const { appName } = app;
		const reqBody: any = { zupeeUserId: uid, gameName, isNetworkLogRequired: true };

		if (!_isNil(mttid)) {
			reqBody.mttid = mttid;
		} else {
			reqBody.ltid = ltid;
			if (!_isNil(cgrid)) {
				reqBody.cgrid = cgrid;
			}
		}
		if (!_isNil(playingAttempt)) {
			reqBody.playingAttempt = Number(playingAttempt);
		}

		if (!_isNil(ludoUserId)) {
			reqBody.ludoUserId = ludoUserId;
		}

		if (!_isNil(pwfInviteId) && !_isNil(gameType)) {
			reqBody.pwfInviteId = pwfInviteId;
			reqBody.gameType = gameType;
		}
		const permission = _find(permissionsConfig, { actionName: ACTION.analyzeTournament });

		const result = await postData({
			endpoint: `/dashboard/analyzeTournament/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.gameplay}-analyzeTournament`,
			queryParams: {
				appName
			},
			body: reqBody,
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		const data = await hydrateData(_get(result, ['data', 'data'], {}), !_isNil(mttid), gameName);
		setAnalyzeData(data);
		setTournamentStartTime(_get(data, VIEW_FIELD_KEYS.TOURNAMENT_START_TIME));
		setTournamentStartTimeInMS(convertTimeStringToUnix(_get(result, ['data', 'data', VIEW_FIELD_KEYS.TOURNAMENT_START_TIME])));
		setTournamentEndTimeInMS(convertTimeStringToUnix(_get(result, ['data', 'data', VIEW_FIELD_KEYS.TOURNAMENT_END_TIME])));
		setRegisteredOnInMS(convertTimeStringToUnix(_get(result, ['data', 'data', VIEW_FIELD_KEYS.REGISTERED_ON])));
		setShowLoader(false);
		setForceUpdateChild(true);
		setGameEndTime(!_isNil(_get(data, 'gameEndTime')) ? _get(data, 'gameEndTime') : _get(data, 'tournamentEndTime'));
		setGameStartTime(!_isNil(_get(data, 'gameStartTime')) ? _get(data, 'gameStartTime') : _get(data, 'tournamentStartTime'));
		setTableId(_get(data, 'tableId'));
		setGameUid(_get(data, 'uid'));
		setRegisteredOn(_get(data, 'registeredOn'));
		setEntryFee(_get(data, 'entryFee'));
		setTransactionId(_get(data, 'userPaymentTransactionId'));
		setInning(_get(data, 'inning'));
		setScore(_get(data, 'score'));
		setResultData(_get(data, 'resultData'));
		setWinnings(_get(data, 'winnings'));
	};

	useEffect(() => {
		fetchData();
	}, []);

	useEffect(() => {
		if (forceUpdate) {
			fetchData();
		}
	}, [forceUpdate]);

	const analyzeTournament = () => {
		const data = formatAnalyzeTournamentData(analyzeData, gameName, mttid);
		return data;
	};

	const getGameplayTimelineData = async () => {
		const data = _get(analyzeData, ['gameAndNetworkLogs'], EMPTY_ARRAY);
		const listData = await formatListData(data);

		return { rowCount: listData.length, data: listData };
	};

	const getRoundInfo = () => {
		const roundInfo = _get(analyzeData, 'roundInfo');
		return { rowCount: roundInfo.length, data: formatListData(roundInfo) };
	};

	return {
		analyzeTournament,
		tournamentStartTime,
		getGameplayTimelineData,
		forceUpdate,
		forceUpdateChild,
		setForceUpdateChild,
		setForceUpdate,
		showLoader,
		getRoundInfo,
		tableId,
		gameEndTime,
		gameStartTime,
		gameUid,
		registeredOn,
		tournamentStartTimeInMS,
		tournamentEndTimeInMS,
		registeredOnInMS,
		entryFee,
		transactionId,
		winnings,
		score,
		resultData,
		inning
	};
};

export default useService;
